import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../Headers/Header';
import Footer from '../Footer/Footer';

const ErrorPage = ({ error: errorProp }) => {
  const location = useLocation();
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if this is a 404 error or Sanity Studio path
  const is404 = (errorProp?.status === 404) || 
               (errorProp?.response?.status === 404) || 
               (location.pathname === '/belsoft-blog') ||
               (errorProp?.statusText === 'Not Found');
  
  // Set up error details
  const title = is404 ? '404' : errorProp?.status || 'Error';
  const subtitle = is404 ? 'Page Not Found' : errorProp?.statusText || 'Something went wrong';
  const description = is404
    ? 'The page you are looking for might have been removed or is temporarily unavailable.'
    : 'We apologize for the inconvenience. Please try again later or contact support if the problem persists.';
    
  // If it's the Sanity Studio route, show a different message
  const isSanityStudio = location.pathname.startsWith('/belsoft-blog');
  const showSanityMessage = isSanityStudio && is404;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Decorative elements */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 h-2"></div>
          
          <div className="p-8 text-center">
            {/* Error code */}
            <div className="text-8xl font-bold text-blue-600 mb-2">
              {title}
            </div>
            
            {/* Main message */}
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {showSanityMessage ? 'Sanity Studio Not Available' : subtitle}
            </h1>
            
            {/* Description */}
            <p className="text-gray-600 mb-8">
              {showSanityMessage 
                ? 'The Sanity Studio is currently not available. Please ensure the Sanity development server is running.'
                : description}
            </p>
            
            {/* Action buttons */}
            <div className="flex flex-col space-y-3 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Home
              </Link>
              
              {isSanityStudio && (
                <a
                  href="http://localhost:3333"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-blue-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Open Sanity Studio Directly
                </a>
              )}
              
              <a
                href="mailto:support@belsoft.com"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Contact Support
              </a>
            </div>
            
            {/* Debug info (only in development) */}
            {process.env.NODE_ENV === 'development' && errorProp && (
              <details className="mt-8 text-left text-sm text-gray-500">
                <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                  Show error details
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded-md overflow-auto max-h-40">
                  <pre className="text-xs">
                    {JSON.stringify({
                      status: errorProp.status,
                      message: errorProp.message || errorProp.statusText,
                      path: location.pathname
                    }, null, 2)}
                  </pre>
                </div>
              </details>
            )}
          </div>
          
          {/* Decorative elements */}
          <div className="bg-gray-50 px-8 py-4 text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact our support team for assistance.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ErrorPage;